import { useSearchParams } from "react-router-dom";
import { Compass, MapPin, MessageCircle, Send } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { EnquiryForm } from "../components/EnquiryForm";
import { SITE, waLink, WA_DEFAULT_MSG } from "../config/site";

const PROCESS = [
  { icon: Send, title: "You share your plans", text: "Fill in the form or message us on WhatsApp." },
  { icon: Compass, title: "We explore options", text: "Our team discusses suitable available options with you." },
  { icon: MapPin, title: "You plan your getaway", text: "Finalise the details together and take the next steps." },
];

export default function PlanTrip() {
  const [params] = useSearchParams();
  const service = params.get("service") || "";

  return (
    <div className="bg-sandlight pt-[72px]">
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
        <SectionHeading
          eyebrow="Plan Your Trip"
          title="Tell Us About Your Journey"
          text="Share your destination, dates and requirements below — or simply message us on WhatsApp. There's no obligation; this is just the start of the conversation."
        />

        <div className="mt-10 grid gap-4 sm:grid-cols-3">
          {PROCESS.map((step, i) => (
            <Reveal key={step.title} delay={i * 0.08}>
              <div className="flex h-full items-start gap-4 rounded-3xl bg-white p-5 ring-1 ring-ocean/5">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-turquoise/10 text-turquoise">
                  <step.icon className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="text-sm font-bold text-ink">{step.title}</h3>
                  <p className="mt-1 text-xs leading-relaxed text-mutedink">{step.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-12 grid items-start gap-8 lg:grid-cols-3">
          <Reveal className="lg:col-span-2">
            <EnquiryForm key={service} initialService={service} />
          </Reveal>

          <Reveal delay={0.15}>
            <div className="rounded-3xl bg-ocean p-7 text-white">
              <MessageCircle className="h-9 w-9 text-gold" />
              <h3 className="mt-4 font-display text-xl font-bold">Prefer to chat?</h3>
              <p className="mt-2 text-sm leading-relaxed text-white/70">
                Message us directly on WhatsApp and we'll take it from there.
              </p>
              <a
                href={waLink(WA_DEFAULT_MSG)}
                target="_blank" rel="noopener noreferrer"
                data-testid="plan-trip-whatsapp-button"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
              >
                <MessageCircle className="h-4 w-4" /> WhatsApp Us
              </a>
              <p className="mt-4 text-xs text-white/50">{SITE.whatsappDisplay}</p>
            </div>

            <div className="mt-5 rounded-3xl bg-sand p-7">
              <h3 className="font-display text-base font-bold text-ink">Good to know</h3>
              <ul className="mt-3 list-disc space-y-2 pl-5 text-xs leading-relaxed text-mutedink">
                <li>Enquiries go straight to the Arora Travel Online team.</li>
                <li>No payment is taken on this website — it's simply the first step.</li>
                <li>The more detail you share, the better we can help.</li>
              </ul>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
